import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { COMMENT_API_PREFIX } from 'shared/api/constants/api-constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { UseMutationClearOptions } from 'shared/types/tanstack-utility-types'
import { commentsKeys } from 'modules/comments/api/key-factory'
import type { CommentsTypeEnum } from 'modules/comments/types'

interface UseDeleteCommentOptions extends Omit<
  UseMutationClearOptions<void, Error, number>,
  'gcTime'
> {
  commentsType: CommentsTypeEnum
  pageId: number
}

export const useDeleteComment = ({
  commentsType,
  pageId,
  ...options
}: UseDeleteCommentOptions): UseMutationResult<void, Error, number> => {
  const queryClient = useQueryClient()
  const { fetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum.delete, void, void>({
    method: RequestMethodsEnum.delete,
  })

  return useMutation({
    mutationKey: commentsKeys.delete(commentsType, pageId),
    mutationFn: async (commentId: number) => fetcher(`${COMMENT_API_PREFIX}/${commentId}`),
    ...options,
    gcTime: Infinity,
    onSuccess: async (...args) => {
      void queryClient.invalidateQueries(
        { queryKey: commentsKeys.resource(commentsType, pageId) },
        { cancelRefetch: false },
      )
      await options.onSuccess?.(...args)
    },
  })
}
